import { gql } from '@apollo/client';
import { useDisclosure } from '@dwarvesf/react-hooks';
import {
  Avatar as MUIAvatar,
  Badge,
  Box,
  Chip,
  IconButton,
  Typography,
} from '@mui/material';
import { Avatar } from 'components/common/AvatarGroup';
import { HighlightableSection } from 'components/common/HighlightableSection';
import { Timestamp } from 'components/common/Timestamp';
import { typography } from 'components/common/Typography/styles';
import { IconBoldEdit } from 'components/icons/components/bold/IconBoldEdit';
import { IconBoldReceiptEdit } from 'components/icons/components/bold/IconBoldReceiptEdit';
import { PostPreview } from 'features/post';
import { SocialListeningRadarActivityDialogView } from 'features/socialListeningRadar';
import { ExpandableText } from 'features/socialMediaListening';
import {
  PostFragmentPostPreviewFragmentDoc,
  RadarEventFragmentRadarEventTimelineItemInternalActivityFragment,
} from 'graphql/generated';
import moment from 'moment';
import { theme } from 'styles/theme';

export const RADAR_EVENT_FRAGMENT_RADAR_EVENT_TIMELINE_ITEM_INTERNAL_ACTIVITY = gql`
  fragment RadarEventFragmentRadarEventTimelineItemInternalActivity on RadarEventModel {
    id
    brandId
    brand {
      id
      name
      creators {
        id
        nickName
        handle
        profilePictureUrl
      }
    }
    createdAt
    creator {
      id
      nickName
      handle
      profilePictureUrl
    }
    radarEventActivities {
      id
      title
      summary
      direction
      createdAt
      dateTime
      createdByUser {
        id
        firstName
        lastName
        email
        avatarUrl
      }
      assets {
        id
        ...PostFragmentPostPreview
      }
      channels {
        id
        name
        color
        bgColor
      }
      createdForCreatorProfiles {
        id
      }
      radarEvents {
        id
      }
    }
  }
  ${PostFragmentPostPreviewFragmentDoc}
`;

export type RadarEventTimelineItemInternalActivityProps = {
  radarEvent: RadarEventFragmentRadarEventTimelineItemInternalActivityFragment;
};

export const RadarEventTimelineItemInternalActivity = (
  props: RadarEventTimelineItemInternalActivityProps,
) => {
  const { radarEvent } = props;

  const radarEventActivity = radarEvent.radarEventActivities[0];
  const {
    isOpen: isRadarActivityDialogOpen,
    onOpen: openRadarActivityDialog,
    onClose: closeRadarActivityDialog,
  } = useDisclosure();

  return (
    <Box
      sx={{
        bgcolor: theme.colors?.utility[250],
        p: theme.spacing(7),
        borderRadius: 3,
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          {radarEventActivity.createdByUser && (
            <Badge
              overlap="circular"
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              badgeContent={
                <MUIAvatar
                  sx={{
                    width: 16,
                    height: 16,
                    bgcolor: theme.colors?.primary.white,
                  }}
                  src={radarEvent.brand.creators?.[0]?.profilePictureUrl || ''}
                />
              }
            >
              <Avatar size={32} user={radarEventActivity.createdByUser} />
            </Badge>
          )}
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography variant="headline-sm">
              {radarEventActivity.createdByUser?.firstName}{' '}
              {radarEventActivity.createdByUser?.lastName} •{' '}
              {radarEvent.brand.name}
            </Typography>{' '}
            <Typography variant="subhead-lg" color={theme.colors?.utility[700]}>
              <Timestamp
                date={moment(radarEventActivity.createdAt)}
                humanized={false}
                format="MMM DD, YYYY • hh:mm A"
              />
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              backgroundColor: 'rgba(35, 6, 3, 0.05)',
              p: theme.spacing(1, 3),
              borderRadius: 5,
            }}
          >
            <IconBoldReceiptEdit size={16} color={theme.colors?.utility[600]} />
            <Typography
              variant="body-xl"
              fontWeight={600}
              color={theme.colors?.utility[800]}
            >
              Activity
            </Typography>
          </Box>

          <IconButton
            size="small"
            sx={{
              backgroundColor: theme.colors?.utility[250],
              borderRadius: 5,
            }}
            onClick={openRadarActivityDialog}
          >
            <IconBoldEdit size={16} color={theme.colors?.utility[700]} />
          </IconButton>
        </Box>
      </Box>

      <HighlightableSection
        sx={{
          borderRadius: 3,
          pt: 5,
          pr: 5,
          bgcolor: theme.colors?.utility[250],
          display: 'flex',
          gap: 8,
          position: 'relative',
          '--highlighted-fade-final-bgcolor': theme.colors?.utility[250],
          scrollMarginTop: 200,
        }}
        sectionKey={`rea-${radarEventActivity.id}`}
      >
        {!!radarEventActivity.assets.length && (
          <Box>
            <Box
              sx={{
                width: 140,
                aspectRatio: '7 / 10',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                }}
              >
                {radarEventActivity.assets.slice(0, 1).map((asset, index) => {
                  return <PostPreview key={asset.id} post={asset} />;
                })}
              </Box>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            flex: 1,
          }}
        >
          <Typography variant="headline-sm">
            {radarEventActivity.title}
          </Typography>
          <ExpandableText
            collapsedHeight={100}
            text={radarEventActivity.summary}
            componentProps={{
              text: {
                sx: {
                  ...typography['subhead-xl'],
                },
              },
              seeMore: {
                label: 'See more',
              },
              seeLess: {
                label: 'Hide',
              },
            }}
          />
        </Box>
      </HighlightableSection>
      {isRadarActivityDialogOpen && (
        <SocialListeningRadarActivityDialogView
          onSave={closeRadarActivityDialog}
          onClose={closeRadarActivityDialog}
          brandId={radarEvent.brandId}
          variant="edit"
          radarEventActivityId={radarEventActivity.id}
          defaultValues={{
            assetIds: radarEventActivity.assets.map((asset) => asset.id),
            createdForCreatorProfileIds:
              radarEventActivity.createdForCreatorProfiles.map(
                (profile) => profile.id,
              ),
            direction: radarEventActivity.direction,
            summary: radarEventActivity.summary,
            title: radarEventActivity.title,
            dateTime: radarEventActivity.dateTime,
            radarEventIds: radarEventActivity.radarEvents.map(
              (radarEvent) => radarEvent.id,
            ),
            radarEventActivityCommunicationChannelIds:
              radarEventActivity.channels.map((channel) => channel.id),
          }}
        />
      )}
    </Box>
  );
};
